import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
  Container,
  Heading,
  ListItem as ChakraListItem,
  Text as CharaText,
  VStack,
  OrderedList,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Link } from '~/common/components/link';
import type { Props as MinimalLayoutProps } from '~/common/layouts/minimal-layout';
import type { Props as NoHeaderFooterLayoutProps } from '~/common/layouts/no-layout';
import { NameConstants } from '~/utils/constants';
import { routes } from '~/utils/routes';
import { Color, FontSize } from '~/utils/styles/enums';

const MinimalLayout = dynamic<MinimalLayoutProps>(() =>
  import('~/common/layouts/minimal-layout').then(
    ({ MinimalLayout }) => MinimalLayout
  )
);

const NoLayout = dynamic<NoHeaderFooterLayoutProps>(() =>
  import('~/common/layouts/no-layout').then(
    ({ NoHeaderFooterLayout }) => NoHeaderFooterLayout
  )
);

export const Page: NextPage = () => {
  const { query } = useRouter();
  const noLayout = query['nolayout'] === '1';
  return (
    <>
      {noLayout ? (
        <NoLayout
          metaData={{
            title: routes['privacy'].title(),
          }}
          pageInnerProps={{
            fullWidth: true,
          }}
          wrapperStyles={{
            background: Color.STRONG_BG,
          }}
        >
          <Content noLayout={noLayout} />
        </NoLayout>
      ) : (
        <MinimalLayout
          metaData={{
            title: routes['privacy'].title(),
          }}
          breadcrumbList={[]}
          pageInnerProps={{
            fullWidth: true,
          }}
          wrapperStyles={{
            background: Color.STRONG_BG,
          }}
        >
          <Content noLayout={noLayout} />
        </MinimalLayout>
      )}
    </>
  );
};

const ExternalLink = ({ url }: { url: string }) => (
  <Link href={url} target="_blank">
    リンク
    <ExternalLinkIcon w="14px" h="14px" color={Color.TINT} mt={-1} ml={1} />
  </Link>
);

const Content = ({ noLayout }: { noLayout: boolean }) => (
  <Container
    padding={{ base: '16px', md: '40px' }}
    background={Color.WHITE}
    borderRadius={
      noLayout
        ? undefined
        : {
            base: 0,
            md: '8px',
          }
    }
    maxWidth={noLayout ? '100%' : '3xl'}
  >
    <VStack spacing="8" alignItems="flex-start">
      <Heading as="h1" size="lg" color={Color.ACCENT_1000}>
        {routes.privacy.title()}
      </Heading>
      <VStack alignItems="flex-start">
        <Text>
          {NameConstants.CORP_NAME}（以下「当社」といいます。）は、「
          {NameConstants.SITE_NAME_EN}」（以下「本
          サービス」といいます。）において、当社がユーザーから取得する「登録者情報」（以下の「１．取得する登録者情報および取得方法について」に規定）の取扱いに関し、本プライバシーポリシー（以下「本ポリシー」といいます。）を策定し、これを遵守いたします。なお、本ポリシーで用いる用語は、別段の定めがある場合を除き、すべて「
          {NameConstants.SITE_NAME_EN}
          サービス利用規約」（https://teller.jp/tos）（以下「本サービス規約」といいます。）に準ずるものとします。
        </Text>
        <Text>
          当社では、当社自身によるパトロールや権利者からの報告によりクリエイターの方々の権利が侵害されていると把握した場合、侵害状況を確認の上で「サービス利用規約」第12条第1項に基づき、直ちに侵害情報を削除いたします。
        </Text>
        <Text>
          権利侵害が行われている事実を発見された方は、情報提供先メールアドレス宛にご連絡いただくか、該当ページの報告ボタンから権利侵害に基づく削除請求をお願いいたします。速やかに順次対応をさせていただきます。
        </Text>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          １．取得する登録者情報および取得方法について
        </Heading>
        <Text>
          本ポリシーにおいて、「登録者情報」とは、本サービス規約第1条第５号に定める「登録アカウント情報」における登録ユーザーの識別に係る個人情報（個人情報保護法第２条第１項に定義される「個人情報」と同義）のほか、本ポリシーに基づき当社が取得する以下の情報をいいます。なお、当社による登録者情報の取得に同意いただけない場合、本サービスの全部または一部が利用できない場合がありますので、予めご了承ください。{' '}
        </Text>
        <OrderedList
          spacing="2"
          marginTop="4"
          pl="5"
          fontSize={FontSize.SIZE_16}
        >
          <ListItem>
            本サービス規約第１条第5号に定める「登録アカウント情報」のほか、本サービスにおけるキャンペーン等の際に登録ユーザーが入力する情報等
          </ListItem>
          <ListItem>
            ログイン日時情報および行動履歴情報（本サービス利用時に自動で生成、保存されるIPアドレス、登録ユーザーからのリクエスト日時等）
          </ListItem>
          <ListItem>
            個人を特定する情報を含まない端末識別情報（端末識別情報を利用した登録ユーザーの履歴情報等）
            <br />
            なお、この場合、登録ユーザー自身が本サービス内で個人情報を入力しない限り、個人を特定することはできず、匿名性は維持されます。
          </ListItem>
          <ListItem>
            登録ユーザーがプラットフォームサービス（Apple、Facebook，Twitter等）から本サービスの登録またはログインをした際に入力したIDおよびパスワードから取得できる名前、ニックネーム、電子メールアドレス、誕生日、性別、プロフィール画像、その他の情報。ただし、当情報は一部例外を除き、第2条4項に定める入力の簡素化のために利用されます。
          </ListItem>
        </OrderedList>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          2．利用目的
        </Heading>
        <Text>
          当社は、取得した登録者情報を、以下の目的のために使用します。
        </Text>
        <OrderedList
          spacing="2"
          marginTop="4"
          pl="5"
          fontSize={FontSize.SIZE_16}
        >
          <ListItem>本サービスの提供のため</ListItem>
          <ListItem>
            本サービスに関する各種事項の連絡や情報提供を行うため
          </ListItem>
          <ListItem>
            本サービスに関するカスタマーサポートを提供する目的で情報を連携、処理し、お客様に各種連絡を行うため
          </ListItem>
          <ListItem>
            本サービスのユーザ登録に際し必要な情報入力を簡素化するため
          </ListItem>
          <ListItem>
            当社もしくは第三者に対する違法行為または本サービス規約または個別規約等に違反する行為の防止のため
          </ListItem>
          <ListItem>
            本サービスの維持・改善または新規サービスの資料とするための匿名化した統計データを作成し、研究開発のために利用若しくは調査結果として公表するため
          </ListItem>
          <ListItem>
            本サービスの利用状況等をサービス改善の目的で調査・分析するため
          </ListItem>
          <ListItem>
            本サービスの利用状況を機械学習によって分析し、興味関心に沿った作品の配信を行うため。
          </ListItem>
          <ListItem>
            本サービスの内容をお客様に合わせてカスタマイズするため。
          </ListItem>
          <ListItem>
            マーケティング調査・分析およびアンケート調査・分析するため
          </ListItem>
          <ListItem>
            調査のご協力依頼や各種イベントへの参加を依頼する目的で連絡を行うため
          </ListItem>
          <ListItem>本サービスに関する代金決済・請求のため</ListItem>
          <ListItem>
            本サービスまたは新規サービスその他サービスに関する情報提供のため
          </ListItem>
          <ListItem>メンテナンスその他の重要なお知らせのため</ListItem>
          <ListItem>
            当社または第三者の広告の配信または表示のため（※
            <Link href="/app_policy" color={Color.TINT}>
              端末識別情報に基づく広告配信についてはこちら
            </Link>
            ）
          </ListItem>
          <ListItem>
            その他利用目的を別途明示して登録者情報を取得する場合における当該明示された当該利用目的のため
          </ListItem>
        </OrderedList>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          ３．第三者への個人情報の提供
        </Heading>
        <Text>
          当社は、登録者情報のうち、個人情報については、個人情報保護法その他の法令に基づき開示が認められる場合を除くほか、あらかじめユーザーご本人（未成年者の場合は法定代理人を含みます。）の同意なしに第三者に提供しません。ただし、以下の場合はこの限りではありません。
        </Text>
        <OrderedList
          spacing="2"
          marginTop="4"
          pl="5"
          fontSize={FontSize.SIZE_16}
        >
          <ListItem>
            当社が、利用目的達成に必要な範囲において個人情報の取扱いの全部または一部を第三者に委託する場合（配送業者、決済代行業者、金融機関、クレジットカード会社、回収代行業者、業務委託先その他の第三者を含みます。）
          </ListItem>
          <ListItem>
            合併、会社分割または事業譲渡その他の事由による事業の承継に伴って個人情報が第三者に提供される場合
          </ListItem>
          <ListItem>
            裁判手続その他の紛争解決手続、捜査機関に対する捜査協力その他公的機関に対する協力において、当社またはユーザーその他の第三者の生命・身体・財産等の権利または利益を守るために情報開示が必要であると当社が判断する場合
          </ListItem>
          <ListItem>
            商品または景品の配送、代金決済、報酬支払、ユーザーご本人からのお問い合わせへの対応、当社からユーザーご本人へのお問い合わせ、関連するアフターサービスの提供等をする場合に開示する場合
          </ListItem>
          <ListItem>
            法令等に基づき、裁判所、行政機関、監督官庁その他公的機関から取得情報を提供するよう求められた場合
          </ListItem>
          <ListItem>
            特定電気通信役務提供者の損害賠償責任の制限及び発信者情報の開示に関する法律に基づき開示請求が行われ、当社が必要だと判断した場合
          </ListItem>
          <ListItem>
            クレジットカード発行会社が行う不正利用検知および不正利用防止のため、当社が取得情報の提供が必要と判断した場合
          </ListItem>
          <ListItem>
            その他、個人情報保護法その他の法令で認められる場合
          </ListItem>
        </OrderedList>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          ４．取得情報の取り扱いに関する第三者への委託
        </Heading>
        <Text>
          当社は、第2条(利用目的)の達成に必要な範囲内において、登録者情報の取扱いの全部または一部を業務委託先に委託することがあります。その際、当社が定める委託先選定基準を満たす者であるかを十分審査し、契約にあたっては取得情報が適正に管理されるよう確保します。
        </Text>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          ５．個人情報の管理のための講じている措置
        </Heading>
        <Text>
          当社は、当社の管理する個人情報につき、合理的な技術的施策、および各従業員に対する啓発活動を行なうことにより、情報紛失、改ざん、漏洩などの防止に努めます。
          <br />
          また、登録者情報を、一般のユーザーがアクセスできない環境下にあるサーバにて安全に保管し、不正アクセス・紛失・破壊・改ざんまたは漏洩が生じないよう努めます。
        </Text>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          ６．個人情報の開示
        </Heading>
        <Text>
          当社は、ユーザーご本人（未成年者の場合は法定代理人を含みます。）から、個人情報保護法の定めに基づき個人情報の開示を求められたときは、ご本人からの請求であることを確認したうえで、ご本人に対して遅滞なく個人情報を開示します（当該個人情報が存在しない場合はその旨を通知します。）。ただし、個人情報保護法その他の法令により、当社が開示義務を負わない場合はこの限りではありません。なお、個人情報の開示につきましては手数料（1件につき2,000円）が発生いたします。
        </Text>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          ７．個人情報の訂正および利用停止
        </Heading>
        <Text>
          当社は、ユーザーご本人（未成年者の場合は法定代理人を含みます。）から以下の各号の理由により、個人情報保護法の定めに基づき個人情報の内容の訂正または利用の停止を求められたときは、ご本人からの請求であることを確認したうえで、遅滞なく必要な調査を行い、その結果に基づき、個人情報の内容の訂正または利用の停止を行い、その旨をご本人に通知します。なお、合理的な理由に基づいて訂正または利用の停止を行わない旨の決定をしたときであっても、ご本人にその旨を通知させていただきます。
        </Text>
        <OrderedList
          spacing="2"
          marginTop="4"
          pl="5"
          fontSize={FontSize.SIZE_16}
        >
          <ListItem>個人情報が真実でないという理由</ListItem>
          <ListItem>
            当社があらかじめ公表した利用目的の範囲を超えて取り扱っている、または偽りその他不正の手段により収集されたものであるという理由
          </ListItem>
        </OrderedList>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          ８．個人情報の保持
        </Heading>
        <OrderedList
          spacing="2"
          marginTop="4"
          pl="5"
          fontSize={FontSize.SIZE_16}
        >
          <ListItem>
            当社は、本サービスの提供及び本ポリシーに定めるその他の目的のために必要な期間、ユーザーの個人情報を保持します。
          </ListItem>
          <ListItem>
            ユーザーへの本サービスの提供やユーザーが本サービスの登録アカウントを削除するなどユーザーとの取引等が終了した後は、当社は、セキュリティ上必要な場合、不正行為防止、または、適用法令を遵守するためにユーザーの個人情報を保持しなければならない場合や、未解決の請求もしくは苦情に対応する場合など、正当な必要性がある場合を除き、ユーザーの個人情報を情報の収集目的に必要な限度を超えて、保存することはありません。
          </ListItem>
        </OrderedList>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          9．本ポリシーの変更
        </Heading>
        <OrderedList
          spacing="2"
          marginTop="4"
          pl="5"
          fontSize={FontSize.SIZE_16}
        >
          <ListItem>
            当社は、ユーザーの登録者情報の管理・運用について継続的な改善に努めるものとし、必要に応じて本ポリシーを見直し、変更できるものとします。
          </ListItem>
          <ListItem>
            当社は、本ポリシーを変更する場合、当社指定の方法で告知します。変更後のプライバシーポリシーは、当社サイト上に表示した改定日から効力を生じるものとします。
          </ListItem>
          <ListItem>
            本ポリシーの変更後も本サービスのご利用を続ける場合は、変更後のプライバシーポリシーに同意したものとみなされます。
          </ListItem>
        </OrderedList>
        <Text>
          本ポリシーの最新版は、常にこのページ（https://teller.jp/privacy）でご覧いただけます。
        </Text>
      </VStack>

      <VStack alignItems="flex-start" width="100%" maxWidth="100%">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          10. お客様情報の外部送信について
        </Heading>

        <Text>
          当社は、本サービスを提供するにあたり、お客様に関する情報を収集し、プライバシー保護を尊重した上で外部事業者に送信することがあります。外部事業者の事業者名・サービス名、外部事業者に送信されるお客様情報の内容、送信される情報の利用目的については、以下より詳細をご確認下さい。
        </Text>

        <GridTable templateColumns="repeat(5, 1fr)" maxWidth="100%">
          {/* Table header */}
          <GridTableHeader>
            <Text>事業者名</Text>
          </GridTableHeader>
          <GridTableHeader>
            <Text>サービス名</Text>
          </GridTableHeader>
          <GridTableHeader>
            <Text>利用目的</Text>
          </GridTableHeader>
          <GridTableHeader>
            <Text>プライバシーポリシーへのリンク</Text>
          </GridTableHeader>
          <GridTableHeader>
            <Text>オプトアウトへの リンク</Text>
          </GridTableHeader>

          {/* Rows */}
          <GridTableColumn rowSpan={4}>Google</GridTableColumn>
          <GridTableColumn wb>Firebase</GridTableColumn>
          <GridTableColumn wb>閲覧の傾向や履歴の分析のため</GridTableColumn>
          <GridTableColumn wb>
            <ExternalLink url="https://policies.google.com/privacy?hl=ja" />
          </GridTableColumn>
          <GridTableColumn wb></GridTableColumn>

          <GridTableColumn wb>Google Analytics</GridTableColumn>
          <GridTableColumn wb>閲覧の傾向や履歴の分析のため</GridTableColumn>
          <GridTableColumn wb>
            <ExternalLink url="https://policies.google.com/privacy?hl=ja" />
          </GridTableColumn>
          <GridTableColumn wb>
            <ExternalLink url="https://tools.google.com/dlpage/gaoptout/" />
          </GridTableColumn>

          <GridTableColumn>Admob</GridTableColumn>
          <GridTableColumn>広告の掲載・配信をするため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://support.google.com/admob/answer/6128543?hl=en" />
          </GridTableColumn>
          <GridTableColumn></GridTableColumn>

          <RowSeparator colSpan={5} />
          {/* Row 2 */}
          <GridTableColumn>Adjust Co., Ltd.</GridTableColumn>
          <GridTableColumn>Adjust</GridTableColumn>
          <GridTableColumn>広告の掲載・配信をするため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://www.adjust.com/terms/privacy-policy/" />
          </GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://www.adjust.com/ja/forget-device/" />
          </GridTableColumn>

          {/* Row 3 */}
          <RowSeparator colSpan={5} />
          <GridTableColumn>Microsoft Corporation</GridTableColumn>
          <GridTableColumn>Clarity</GridTableColumn>
          <GridTableColumn>閲覧の傾向や履歴の分析のため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://privacy.microsoft.com/ja-jp/privacystatement" />
          </GridTableColumn>
          <GridTableColumn></GridTableColumn>

          {/* Row 4 */}
          <RowSeparator colSpan={5} />
          <GridTableColumn>BytePlus Pte. Ltd.</GridTableColumn>
          <GridTableColumn>BytePlus DI</GridTableColumn>
          <GridTableColumn>閲覧の傾向や履歴の分析のため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://docs.byteplus.com/en/legal/docs/privacy-policy" />
          </GridTableColumn>
          <GridTableColumn></GridTableColumn>

          {/* Row 5 */}
          <RowSeparator colSpan={5} />
          <GridTableColumn>Bytedance Pte. Ltd.</GridTableColumn>
          <GridTableColumn>Pangle</GridTableColumn>
          <GridTableColumn>広告の掲載・配信をするため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://www.pangleglobal.com/jp/privacy" />
          </GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://www.pangleglobal.com/jp/global-did" />
          </GridTableColumn>

          {/* Row 6 */}
          <RowSeparator colSpan={5} />
          <GridTableColumn>Sentry</GridTableColumn>
          <GridTableColumn>Sentry</GridTableColumn>
          <GridTableColumn>不具合の分析のため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://sentry.io/privacy/" />
          </GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://sentry.io/contact/gdpr/" />
          </GridTableColumn>

          {/* Row 7 */}
          <RowSeparator colSpan={5} />
          <GridTableColumn>AppLovin Corporation</GridTableColumn>
          <GridTableColumn>AppLovin</GridTableColumn>
          <GridTableColumn>広告の掲載・配信をするため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://www.applovin.com/privacy-jp/" />
          </GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://dash.applovin.com/p/how-applovin-shows-you-ads?_gl=1*1b0kue6*_ga*MTk2MTE1NjEwMy4xNjk0Njc1ODA4*_ga_0JQJREV5DQ*MTY5NDY3NTgwOC4xLjEuMTY5NDY3NTg4MC41MC4wLjA.&_ga=2.194140461.1079299189.1694675808-1961156103.1694675808" />
          </GridTableColumn>

          {/* Row 8 */}
          <RowSeparator colSpan={5} />
          <GridTableColumn rowSpan={2}>Unity Software Inc.</GridTableColumn>
          <GridTableColumn wb>Unity Ads</GridTableColumn>
          <GridTableColumn wb>広告の掲載・配信をするため</GridTableColumn>
          <GridTableColumn wb>
            <ExternalLink url="https://unity.com/ja/legal/privacy-policy" />
          </GridTableColumn>
          <GridTableColumn wb>
            <ExternalLink url="https://unity.com/ja/data-request" />
          </GridTableColumn>

          <GridTableColumn>ironSource</GridTableColumn>
          <GridTableColumn>広告の掲載・配信をするため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://developers.is.com/ironsource-mobile/air/ironsource-mobile-privacy-policy/#step-1" />
          </GridTableColumn>
          <GridTableColumn></GridTableColumn>

          {/* Row 9 */}
          <RowSeparator colSpan={5} />
          <GridTableColumn>Meta Platforms, Inc.</GridTableColumn>
          <GridTableColumn>Meta</GridTableColumn>
          <GridTableColumn>広告の掲載・配信をするため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://www.facebook.com/privacy/policy" />
          </GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://www.facebook.com/privacy/guide/ads" />
          </GridTableColumn>

          {/* Row 10 */}
          <RowSeparator colSpan={5} />
          <GridTableColumn>MicroAd, Inc.</GridTableColumn>
          <GridTableColumn>MicroAd</GridTableColumn>
          <GridTableColumn>広告の掲載・配信をするため</GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://www.microad.co.jp/privacy/privacypolicy_en.html" />
          </GridTableColumn>
          <GridTableColumn>
            <ExternalLink url="https://send.microad.jp/w3c/" />
          </GridTableColumn>
        </GridTable>
      </VStack>

      <VStack alignItems="flex-start">
        <Heading
          as="h2"
          mb={5}
          fontSize={FontSize.SIZE_20}
          color={Color.ACCENT_1000}
        >
          11．お問合わせ先
        </Heading>

        <Text>
          本ポリシーに関するお問合わせ並びに保有個人データの取り扱いに関する苦情の申出については、以下の窓口でお受けしています。
          <br />
          本ポリシーお問い合わせ窓口・保有個人データ苦情受付窓口
          <br />
          support[@]tellernovel.com （[]を外してお送りください）
          <br />
          <br />
          {NameConstants.CORP_NAME}
          <br />
          代表取締役 蜂谷 宣人
          <br />
          https://teller.jp/corp
        </Text>
      </VStack>

      <VStack alignItems="flex-start">
        <Text>附則</Text>
        <Text>制定：2017年7月1日</Text>
        <Text>改訂：2018年3月27日</Text>
        <Text>改訂：2019年6月24日</Text>
        <Text>改訂：2022年4月1日</Text>
        <Text>改訂：2023年10月31日</Text>
        <Text>改訂：2024年4月26日</Text>
      </VStack>
    </VStack>
  </Container>
);

const Text = styled(CharaText)`
  color: ${Color.ACCENT_700};
  font-size: ${FontSize.SIZE_16};
  word-break: break-all;
  font-family: 'Avenir Next', 'Hiragino Kaku Gothic Pro', 'ヒラギノ角ゴ Pro W3',
    'Hiragino Sans', Meiryo, メイリオ, Osaka, 'MS PGothic', arial, helvetica,
    sans-serif;
  line-height: 1.7;
`;

const ListItem = styled(ChakraListItem)`
  color: ${Color.ACCENT_700};
  font-size: 1rem;
  word-break: break-all;
  font-family: 'Avenir Next', 'Hiragino Kaku Gothic Pro', 'ヒラギノ角ゴ Pro W3',
    'Hiragino Sans', Meiryo, メイリオ, Osaka, 'MS PGothic', arial, helvetica,
    sans-serif;
  line-height: 1.7;
`;

const GridTable = styled(Grid)`
  border: 1px solid ${Color.ACCENT_50};
  border-radius: 4px;
  font-size: ${FontSize.SIZE_12};
`;

const GridTableHeader = styled(GridItem)`
  padding: 5px;
  font-weight: bold;
  border-bottom: 1px solid ${Color.ACCENT_50};
  display: flex;
  align-items: center;
  background-color: ${Color.ACCENT_50};

  > p {
    font-size: ${FontSize.SIZE_12};
  }
`;

const GridTableColumn = styled(GridItem)<{ wb?: boolean }>`
  display: flex;
  align-items: center;
  max-width: 200px;
  padding: 5px 2px 5px 5px;

  ${({ wb }) =>
    wb &&
    `
      border-bottom: 1px solid ${Color.ACCENT_50};
    `};

  > a {
    color: ${Color.TINT};
    text-align: center;
    display: block;
    width: 100%;
  }
`;

const RowSeparator = styled(GridItem)`
  border-top: 1px solid ${Color.ACCENT_50};
`;
